import {
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalFooter,
  ModalCloseButton,
  Button,
  FormControl,
  FormLabel,
  Input,
  Textarea,
  Select,
  NumberInput,
  NumberInputField,
  NumberInputStepper,
  NumberIncrementStepper,
  NumberDecrementStepper,
  Alert,
  AlertIcon,
  Stack,
  Text,
  useToast,
  Drawer,
  DrawerOverlay,
  DrawerContent,
  DrawerBody,
  DrawerHeader,
  DrawerCloseButton,
  useMediaQuery,
} from '@chakra-ui/react';
import { useState, useEffect } from 'react';
import { ITitleItem, ITitleSubmissionItem } from '../../../../services/hooks/TitleSubmissions/types';
import { useDeclareTitleItem, useUpdateTitleItem } from '../../../../services/hooks/TitleSubmissions/useTitleSubmissionItems';

interface IOwnProps {
  isOpen: boolean;
  onClose: () => void;
  item: ITitleItem;
  existing: ITitleSubmissionItem | undefined;
  submissionId: string;
}

const TitleItemDeclarationModal: React.FC<IOwnProps> = ({
  isOpen,
  onClose,
  item,
  existing,
  submissionId,
}) => {
  const [isMobile] = useMediaQuery('(max-width: 1024px)');
  const toast = useToast();
  const isEdit = !!existing;

  const declareItem = useDeclareTitleItem(submissionId);
  const updateItem = useUpdateTitleItem(submissionId, existing?.id ?? '');

  const [declaredQuantity, setDeclaredQuantity] = useState<number>(
    existing?.declared_quantity ?? 1
  );
  const [qualisLevel, setQualisLevel] = useState<string>(existing?.qualis_level ?? '');
  const [scope, setScope] = useState<string>(existing?.scope ?? '');
  const [selectionType, setSelectionType] = useState<string>(existing?.selection_type ?? '');
  const [observation, setObservation] = useState<string>(existing?.observation ?? '');

  useEffect(() => {
    if (isOpen) {
      setDeclaredQuantity(existing?.declared_quantity ?? 1);
      setQualisLevel(existing?.qualis_level ?? '');
      setScope(existing?.scope ?? '');
      setSelectionType(existing?.selection_type ?? '');
      setObservation(existing?.observation ?? '');
    }
  }, [isOpen, existing]);

  const handleSave = async () => {
    try {
      if (isEdit) {
        await updateItem.mutateAsync({
          declared_quantity: item.scoring_type === 'fixed' ? 1 : declaredQuantity,
          qualis_level: qualisLevel || undefined,
          scope: scope || undefined,
          selection_type: selectionType || undefined,
          observation: observation || undefined,
        });
      } else {
        await declareItem.mutateAsync({
          title_item: item.id,
          declared_quantity: item.scoring_type === 'fixed' ? 1 : declaredQuantity,
          qualis_level: qualisLevel || undefined,
          scope: scope || undefined,
          selection_type: selectionType || undefined,
          observation: observation || undefined,
        });
      }
      toast({
        title: isEdit ? 'Item atualizado' : 'Item declarado',
        status: 'success',
        duration: 3000,
        isClosable: true,
      });
      onClose();
    } catch (err) {
      const error = err as { response?: { data?: { detail?: string } } };
      toast({
        title: 'Erro ao salvar',
        description: error?.response?.data?.detail || 'Ocorreu um erro ao salvar o item.',
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    }
  };

  const isSubmitting = declareItem.isPending || updateItem.isPending;

  const qualisOptions = item.scoring_table_json ? Object.keys(item.scoring_table_json) : [];
  const axesOptions = item.scoring_attributes_json?.axes ?? [];

  const formBody = (
    <Stack spacing={4}>
      {item.required_documents_json.length > 0 && (
        <Alert status="info" borderRadius="md">
          <AlertIcon />
          <Stack spacing={0}>
            <Text fontWeight="bold" fontSize="sm">
              Documentos necessários:
            </Text>
            {item.required_documents_json.map((doc: string, idx: number) => (
              <Text key={idx} fontSize="sm">
                • {doc}
              </Text>
            ))}
          </Stack>
        </Alert>
      )}

      {/* per_qualis / classified */}
      {(item.scoring_type === 'per_qualis' || item.scoring_type === 'classified') && (
        <FormControl isRequired>
          <FormLabel fontSize="sm">Nível Qualis</FormLabel>
          <Select
            placeholder="Selecione o nível"
            value={qualisLevel}
            onChange={(e) => setQualisLevel(e.target.value)}
            size="sm"
          >
            {qualisOptions.map((key) => (
              <option key={key} value={key}>
                {key}
              </option>
            ))}
          </Select>
        </FormControl>
      )}

      {/* per_attributes */}
      {item.scoring_type === 'per_attributes' && (
        <>
          <FormControl isRequired>
            <FormLabel fontSize="sm">Escopo</FormLabel>
            <Input
              size="sm"
              value={scope}
              onChange={(e) => setScope(e.target.value)}
              placeholder="Ex: internacional"
            />
          </FormControl>
          <FormControl isRequired>
            <FormLabel fontSize="sm">Tipo de Seleção</FormLabel>
            <Select
              placeholder="Selecione"
              value={selectionType}
              onChange={(e) => setSelectionType(e.target.value)}
              size="sm"
            >
              {axesOptions.map((axis: string) => (
                <option key={axis} value={axis}>
                  {axis}
                </option>
              ))}
            </Select>
          </FormControl>
        </>
      )}

      {/* Quantidade — oculta para 'fixed' */}
      {item.scoring_type !== 'fixed' && (
        <FormControl isRequired>
          <FormLabel fontSize="sm">Quantidade declarada</FormLabel>
          <NumberInput
            min={1}
            max={item.max_quantity ?? undefined}
            value={declaredQuantity}
            onChange={(_, val) => setDeclaredQuantity(isNaN(val) ? 1 : val)}
            size="sm"
          >
            <NumberInputField />
            <NumberInputStepper>
              <NumberIncrementStepper />
              <NumberDecrementStepper />
            </NumberInputStepper>
          </NumberInput>
          {item.max_quantity && (
            <Text fontSize="xs" color="gray.500" mt={1}>
              Máximo: {item.max_quantity}
            </Text>
          )}
        </FormControl>
      )}

      {/* Observação — comum a todos */}
      <FormControl>
        <FormLabel fontSize="sm">Observação (opcional)</FormLabel>
        <Textarea
          size="sm"
          value={observation}
          onChange={(e) => setObservation(e.target.value)}
          placeholder="Informações adicionais..."
          rows={3}
        />
      </FormControl>
    </Stack>
  );

  const title = isEdit ? `Editar: ${item.name}` : `Declarar: ${item.name}`;
  const footer = (
    <>
      <Button variant="ghost" size="sm" onClick={onClose} isDisabled={isSubmitting}>
        Cancelar
      </Button>
      <Button
        colorScheme="blue"
        size="sm"
        ml={3}
        onClick={handleSave}
        isLoading={isSubmitting}
      >
        Salvar
      </Button>
    </>
  );

  if (isMobile) {
    return (
      <Drawer placement="bottom" isOpen={isOpen} onClose={onClose}>
        <DrawerOverlay />
        <DrawerContent maxH="90%" borderTopRadius="lg">
          <DrawerCloseButton />
          <DrawerHeader fontSize="md" borderBottomWidth="1px">
            {title}
          </DrawerHeader>
          <DrawerBody py={4}>{formBody}</DrawerBody>
          <Stack direction="row" justify="flex-end" p={4} borderTopWidth="1px">
            {footer}
          </Stack>
        </DrawerContent>
      </Drawer>
    );
  }

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="md" isCentered>
      <ModalOverlay />
      <ModalContent>
        <ModalHeader fontSize="md">{title}</ModalHeader>
        <ModalCloseButton />
        <ModalBody>{formBody}</ModalBody>
        <ModalFooter>{footer}</ModalFooter>
      </ModalContent>
    </Modal>
  );
};

export default TitleItemDeclarationModal;
